/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import downloadImg from 'dndimages/download-icon.png';
import Monster from 'model/monster/Monster';
import { FantasyGroundService } from 'services/FantasyGroundService';
import { fetchImageAsDataUrl, convertImageToWebpBlob } from 'utils/ImageUtils';
import './DownloadVttFormatButton.css';

interface ExportAllToVttButtonProps {
  monsters: Monster[];
}

type VttType = 'fantasy-grounds' | 'roll20' | 'foundry';

interface VttOption {
  id: VttType;
  name: string;
  abbreviation: string;
  enabled: boolean;
}

const vttOptions: VttOption[] = [
  { id: 'fantasy-grounds', name: 'Fantasy Grounds', abbreviation: 'FG',      enabled: true },
  { id: 'roll20',          name: 'Roll20',          abbreviation: 'R20',     enabled: false },
  { id: 'foundry',         name: 'Foundry VTT',     abbreviation: 'Foundry', enabled: false },
];

const ExportAllToVttButton: React.FC<ExportAllToVttButtonProps> = ({ monsters }) => {
  const [selectedVtt, setSelectedVtt] = useState<VttType>('fantasy-grounds');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load saved preference
  useEffect(() => {
    const saved = localStorage.getItem('preferredVtt');
    if (saved && vttOptions.some(opt => opt.id === saved)) {
      setSelectedVtt(saved as VttType);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleExportAll = async () => {
    if (!monsters || monsters.length === 0) {
      alert('No monsters to export');
      return;
    }

    const selectedOption = vttOptions.find(opt => opt.id === selectedVtt);
    if (!selectedOption?.enabled) {
      alert(`${selectedOption?.name} export is coming soon!`);
      return;
    }

    try {
      setIsExporting(true);
      setProgress({ current: 0, total: monsters.length });

      const zip = new JSZip();

      // Process all monsters in parallel
      const processedMonsters = await Promise.all(
        monsters.map(async (monster, index) => {
          // Ensure arrays are properly initialized (monsters from DB might be plain objects)
          const safeMonster = {
            ...monster,
            traits: Array.isArray(monster.traits) ? monster.traits : [],
            spells: Array.isArray(monster.spells) ? monster.spells : [],
            actions: Array.isArray(monster.actions) ? monster.actions : [],
            reactions: Array.isArray(monster.reactions) ? monster.reactions : [],
            legendary_actions: Array.isArray(monster.legendary_actions) ? monster.legendary_actions : [],
            lair_actions: Array.isArray(monster.lair_actions) ? monster.lair_actions : [],
            bonus_actions: Array.isArray(monster.bonus_actions) ? monster.bonus_actions : [],
          };

          let imageSrc = monster.image?.src;

          // If image is a Storage URL, fetch and convert to data URL
          if (imageSrc && (imageSrc.startsWith('http://') || imageSrc.startsWith('https://'))) {
            console.log(`Fetching Storage image for ${monster.name}...`);
            const dataUrl = await fetchImageAsDataUrl(imageSrc);
            if (dataUrl) {
              imageSrc = dataUrl;
            }
          }

          let content: string = '';
          let fileExtension: string = '';

          switch (selectedVtt) {
            case 'fantasy-grounds':
              content = await FantasyGroundService.getMonsterXml(safeMonster as Monster);
              fileExtension = 'xml';
              break;
            case 'roll20':
              // Future implementation
              return null;
            case 'foundry':
              // Future implementation
              return null;
          }

          // Convert image to webp if available
          let imageBlob: Blob | null = null;
          if (imageSrc) {
            imageBlob = await convertImageToWebpBlob(imageSrc);
          }

          // Update progress
          setProgress({ current: index + 1, total: monsters.length });

          return {
            name: monster.name,
            content,
            fileExtension,
            imageBlob
          };
        })
      );

      // Add all processed monsters and images to ZIP
      processedMonsters.forEach((processed) => {
        if (!processed || !processed.content || !processed.name?.trim()) {
          return;
        }

        const safeName = FantasyGroundService.sanitizeFilename(processed.name);

        // Add the monster file (XML, etc.)
        zip.file(`${safeName}.${processed.fileExtension}`, processed.content);

        // Add the image as webp if available
        if (processed.imageBlob) {
          zip.file(`${safeName}.webp`, processed.imageBlob);
        }
      });

      // Generate the ZIP file
      const blob = await zip.generateAsync({ type: 'blob' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `monsters-${selectedVtt}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating VTT export ZIP file:', error);
      alert('Failed to create VTT export ZIP file. Please try again.');
    } finally {
      setIsExporting(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleVttSelect = (vttId: VttType) => {
    const option = vttOptions.find(opt => opt.id === vttId);
    if (!option?.enabled) {
      alert(`${option?.name} export is coming soon!`);
      return;
    }

    setSelectedVtt(vttId);
    localStorage.setItem('preferredVtt', vttId);
    setIsDropdownOpen(false);
  };

  const selectedOption = vttOptions.find(opt => opt.id === selectedVtt)!;

  return (
    <div className="vtt-button-container" ref={dropdownRef}>
      <button
        onClick={handleExportAll}
        className="vtt-export-button-main"
        disabled={isExporting}
        title={isExporting ? 'Exporting monsters...' : `Export all monsters to ${selectedOption.name} format`}
        style={{fontSize: 'x-small', padding: '2px'}}
      >
        <img src={downloadImg} alt="Export all to VTT" width="16px" style={{paddingLeft: '8px'}}/>
        {isExporting ? `Exporting... (${progress.current}/${progress.total})` : `Export all to ${selectedOption.abbreviation}`}
      </button>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="vtt-export-button-dropdown"
        title="Choose VTT platform"
        disabled={isExporting}
      >▼</button>

      {isDropdownOpen && (
        <div className="vtt-dropdown-menu">
          {vttOptions.map(option => (
            <div
              key={option.id}
              className={`vtt-dropdown-item ${option.id === selectedVtt ? 'selected' : ''} ${!option.enabled ? 'disabled' : ''}`}
              onClick={() => handleVttSelect(option.id)}
            >
              <span className="vtt-name">{option.name}</span>
              {option.id === selectedVtt && <span className="vtt-checkmark">✓</span>}
              {!option.enabled && <span className="vtt-badge">Coming Soon</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportAllToVttButton;
